import React from "react";

import api from "api";
import {useSelector} from "react-redux";
import {selectDatabaseName} from "reducers/client";
import {useAsyncFunction} from "helpers/useAsyncFunction";

export type DatasetFilters = Record<
  string,
  {
    displayName: string;
    possibleValues: { label: string; value: any; parent: null | string; ancestors: null | string; }[];
  }
>;

export type TimePeriodFilter = {
  displayName: string;
  possibleValues: {
    label: string;
    value: { startDate: string; endDate: string };
  }[];
};

export type TimePeriodFilters = {
  Yearly: TimePeriodFilter;
  YearToDate: TimePeriodFilter;
  Quarterly: TimePeriodFilter;
  Monthly: TimePeriodFilter;
  Weekly: TimePeriodFilter;
};

export type GeneralFiltersDetails = {
  datasetFilters: DatasetFilters;
  timePeriodFilters: TimePeriodFilters;
  defaultFilterPeriod: string;
};

type ReviewFeedFilterDetails = {
  classificationFilters: {
    [key: string]: {
      displayName: string;
      possibleValues: { label: string; value: string }[];
    };
  };
  reviewFilters: {
    [key: string]: {
      displayName: string;
      possibleValues: { label: string; value: string }[];
    };
  };
  staffFilters: {
    [key: string]: {
      displayName: string;
      possibleValues: { label: string; value: string }[];
    };
  };
  reviewSourceFilters: {
    [key: string]: {
      displayName: string;
      possibleValues: { label: string; value: string }[];
    };
  };
  questionNameFilters: {
    [key: string]: {
      displayName: string;
      possibleValues: { label: string; value: string }[];
    };
  };
};

type CommentFiltersDetails = {
  classificationFilters: {
    [key: string]: {
      displayName: string;
      possibleValues: { label: string; value: string }[];
    };
  };
  adjectiveFilters: {
    [key: string]: {
      displayName: string;
      possibleValues: { label: string; value: string }[];
    };
  };
  nounFilters: {
    [key: string]: {
      displayName: string;
      possibleValues: { label: string; value: string }[];
    };
  };
  staffFilters: {
    [key: string]: {
      displayName: string;
      possibleValues: { label: string; value: string }[];
    };
  };
  reviewSourceFilters: {
    [key: string]: {
      displayName: string;
      possibleValues: { label: string; value: string }[];
    };
  };
  questionNameFilters: {
    [key: string]: {
      displayName: string;
      possibleValues: { label: string; value: string }[];
    };
  };
};

export type GeneralFiltersData = {
  errors: unknown[];
  details: GeneralFiltersDetails;
};

export type ReviewFiltersData = {
  errors: unknown[];
  details: ReviewFeedFilterDetails;
};

export type CommentAnalysisFiltersData = {
  errors: unknown[];
  details: CommentFiltersDetails;
};

const generalFiltersUrl =
  "/api/v1/userrole/portalconfigurationmodule/getgeneralfilters";

const reviewFeedFiltersURL =
  "/api/v1/userrole/portalconfigurationmodule/getreviewfeedfilters";

const commentAnalysisFiltersURL =
  "/api/v1/userrole/portalconfigurationmodule/getcommentanalysisfilters";

async function fetchGeneralFilters(
  projectId: number,
  auroraDatabaseName?: string
) {
  const { data } = await api.get<GeneralFiltersData>(generalFiltersUrl, {
    params: {
      projectId,
      auroraDatabaseName,
    },
  });

  if (data.errors.length > 0)
    throw new Error("Could not fetch general filter options.");

  return data.details;
}

async function fetchReviewFeedFilters(
  projectId: number,
  auroraDatabaseName: string
) {
  const { data } = await api.get<ReviewFiltersData>(reviewFeedFiltersURL, {
    params: {
      projectId,
      auroraDatabaseName,
    },
  });

  if (data.errors.length > 0)
    throw new Error("Could not fetch review feed filter options.");

  return data.details;
}

async function fetchCommentAnalysisFilters(
  projectId: number,
  auroraDatabaseName: string
) {
  const { data } = await api.get<CommentAnalysisFiltersData>(
    commentAnalysisFiltersURL,
    {
      params: {
        projectId,
        auroraDatabaseName,
      },
    }
  );

  if (data.errors.length > 0) {
    throw new Error("Could not fetch comment analysis filter options. ");
  }

  return data.details;
}

export function useGetFilterDetails(
  projectId: number,
  commentAnalysis?: boolean,
  reviewFeed?: boolean
) {
  const auroraDatabaseName = useSelector(selectDatabaseName);

  const getFilters = React.useCallback(
    () =>
      Promise.all([
        fetchGeneralFilters(projectId, auroraDatabaseName),
        commentAnalysis
          ? fetchCommentAnalysisFilters(projectId, auroraDatabaseName)
          : undefined,
        reviewFeed
          ? fetchReviewFeedFilters(projectId, auroraDatabaseName)
          : undefined,
      ]).then(([general, comment, review]) => ({ general, comment, review })),
    [projectId, auroraDatabaseName, commentAnalysis, reviewFeed]
  );

  const filters = useAsyncFunction(getFilters);

  return filters;
}

export type FilterDetails = ReturnType<typeof useGetFilterDetails>;
